import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight, ArrowLeft, MapPin, Heart, Download, HandHeart,
  DollarSign, Gift, Briefcase, Facebook, Instagram, Link as LinkIcon, Check, FileText,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import RibbonRain from "@/components/RibbonRain";
import PartnerForm from "@/components/PartnerForm";
import useSEO from "@/hooks/useSEO";

const SHARE_URL = "https://tandemtfoundation.org/foundation/new-beginnings";

const BIN_PARTNERS = [
  { name: "IMX Pilates & Fitness South Shore", address: "6150 Paseo Al Mar Blvd #101, Apollo Beach, FL 33572", status: "October Host · DV Awareness Month", image: "/brand/partners/imx-pilates-fitness-south-shore.jpg" },
  { name: "Beachy Bubbles Bath Co", address: "111 Flamingo Dr, Suite 4, Apollo Beach, FL 33572", status: "October Host · DV Awareness Month", image: "/brand/partners/beachy-bubbles-bath-co.jpg" },
  { name: "Pursuing Wellness", address: "200 Frandorson Cir #200, Apollo Beach, FL 33572", status: "October Host · DV Awareness Month", image: "/brand/partners/pursuing-wellness.jpg" },
  { name: "Talk the Talk Pediatric Therapy", address: "1312 Apollo Beach Blvd Ste M, Apollo Beach, FL 33572", status: "October Host · DV Awareness Month", image: "/brand/partners/talk-the-talk-pediatric-therapy.jpg" },
  { name: "Tampa Bay Cake Company", address: "1312 Apollo Beach Blvd, Suite G, Apollo Beach, FL 33572", status: "October Host · DV Awareness Month", image: "/brand/partners/tampa-bay-cake-company.jpg" },
  { name: "More Locations — Announcing Soon", address: "New storefronts joining across the community", status: "Your business here", image: null },
];

const SEEKING = [
  { icon: DollarSign, tag: "Cash Sponsorships", body: "To purchase high-priority items and directly support program needs for mothers and children." },
  { icon: Gift, tag: "In-Kind Donations", body: "Personal care products, household essentials, comfort items, children's items, gift cards and more." },
  { icon: Briefcase, tag: "Business Partners", body: "Companies willing to host a collection, sponsor supplies, provide services or help amplify the initiative." },
];

const COMMITMENTS = [
  { term: "3-Month Commitment", body: "Helps launch and sustain key programs." },
  { term: "6-Month Commitment", body: "Provides ongoing support and stability for families." },
  { term: "12-Month Commitment", body: "Creates lasting change and helps expand our reach throughout the year." },
];

const BENEFITS = [
  "Logo on the back of event shirts",
  "Logo and feature on our website",
  "Recognition on social media",
  "Spotlight in promotional materials",
  "Public recognition at events and in the community",
];

function Ribbon({ className = "", style }) {
  return (
    <svg viewBox="0 0 64 88" className={className} style={style} fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M32 4C23 17 23 30 32 42C41 30 41 17 32 4Z" />
      <path d="M27 39L17 82" />
      <path d="M37 39L47 82" />
    </svg>
  );
}

function scrollToForm() {
  const el = document.getElementById("partner-form-section");
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

export default function NewBeginnings() {
  useSEO({
    title: "New Beginnings — DV Awareness (October) | The Tandem T Foundation",
    description: "New Beginnings supports domestic violence survivors and their children. This October — 31 Days, 31 Moms. Become a community partner with The Tandem T Foundation in Tampa Bay.",
    path: "/foundation/new-beginnings",
  });

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}`, "_blank", "noopener,noreferrer,width=640,height=640");
  };
  const shareInstagram = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); toast.success("Link copied — paste it into your Instagram post or story."); }
    catch { toast.message("Copy this link for Instagram", { description: SHARE_URL }); }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); toast.success("Link copied to clipboard."); }
    catch { toast.message("Copy this link", { description: SHARE_URL }); }
  };

  return (
    <div data-testid="page-new-beginnings" className="relative overflow-hidden">
      <RibbonRain />

      {/* soft purple glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full z-0"
        style={{ background: "radial-gradient(circle, rgba(147,51,234,0.16), transparent 62%)" }} aria-hidden="true" />

      {/* HERO */}
      <section className="relative z-10 pt-40 pb-16 md:pt-48 md:pb-20 px-6 md:px-10" data-testid="dv-hero">
        <div className="max-w-[1100px] mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-purple-400/40 rounded-full bg-purple-500/10">
              <Ribbon className="w-4 h-5 text-purple-300" />
              <span className="overline text-purple-200">New Beginnings · October — DV Awareness Month</span>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-serif font-light text-white text-5xl md:text-7xl lg:text-8xl mt-8 leading-[1.02] tracking-tight">
              Awareness is the beginning.{" "}
              <span className="text-purple-300 italic">Community is the answer.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="overline text-purple-200 mt-8">Our Mission — New Beginnings</p>
            <p className="text-white/80 font-light leading-relaxed mt-4 max-w-2xl mx-auto text-lg">
              New Beginnings supports <em className="italic text-white">domestic violence survivors and their children</em> —
              providing resources, practical support and encouragement to help families move from surviving to thriving.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button onClick={scrollToForm} data-testid="dv-become-partner"
                className="group inline-flex items-center gap-3 overline px-8 py-5 bg-purple-500 text-white hover:bg-purple-400 transition-colors">
                Become a Community Partner <ArrowUpRight size={16} className="hover-arrow" />
              </button>
              <Link to="/foundation/donate"
                className="group inline-flex items-center gap-3 overline px-8 py-5 border border-purple-400/50 text-white hover:bg-purple-500 hover:border-purple-500 transition-colors">
                Donate This October <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 31 DAYS 31 MOMS */}
      <section className="relative z-10 py-16 md:py-24 px-6 md:px-10 border-t border-purple-400/15" data-testid="dv-31moms">
        <div className="max-w-[1100px] mx-auto text-center">
          <Reveal>
            <p className="overline text-purple-200">The 31 Moms Initiative</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight">
              31 Days. <span className="text-purple-300 italic">31 Moms.</span>
            </h2>
            <p className="text-white/60 font-light mt-4 text-lg">One community showing them they matter.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/75 font-light leading-relaxed mt-8 max-w-3xl mx-auto">
              This October, Tandem T Foundation is mobilizing our community to support mothers and children
              impacted by domestic violence. Our 31 Moms initiative brings together local businesses,
              organizations and individuals to provide hope, resources and encouragement to families as they
              rebuild their lives. While October shines a light on this cause, our commitment continues
              year-round — because healing and stability don&rsquo;t end on October 31st.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FLYER + SOCIAL SHARE */}
      <section className="relative z-10 py-16 md:py-24 px-6 md:px-10 border-t border-purple-400/15" data-testid="dv-flyer">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="pointer-events-none absolute -inset-6 rounded-3xl z-0"
                style={{ background: "radial-gradient(circle, rgba(168,85,247,0.25), transparent 70%)" }} aria-hidden="true" />
              <img src="/flyer/nb-flyer.png" alt="Tandem T Foundation — 31 Days, 31 Moms Domestic Violence Awareness flyer"
                data-testid="dv-flyer-image" className="relative z-10 w-full h-auto border border-purple-400/30 shadow-2xl" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3">
                <Ribbon className="w-6 h-8 text-purple-400" />
                <p className="overline text-purple-200">Share the Word</p>
              </div>
              <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-5 leading-tight tracking-tight">
                Print it. Post it. <span className="text-purple-300 italic">Pass it on.</span>
              </h2>
              <p className="text-white/70 font-light leading-relaxed mt-6 max-w-xl">
                Every share helps a family know they&rsquo;re not alone. Share our flyer to Facebook or Instagram
                in one tap, or download it to print and hang in your storefront.
              </p>

              {/* Social share buttons */}
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={shareFacebook} data-testid="share-facebook"
                  className="group inline-flex items-center gap-2 overline px-6 py-4 bg-purple-500 text-white hover:bg-purple-400 transition-colors">
                  <Facebook size={16} /> Share on Facebook
                </button>
                <button onClick={shareInstagram} data-testid="share-instagram"
                  className="group inline-flex items-center gap-2 overline px-6 py-4 border border-purple-400/50 text-white hover:bg-purple-500 hover:border-purple-500 transition-colors">
                  <Instagram size={16} /> Share on Instagram
                </button>
                <button onClick={copyLink} data-testid="share-copy"
                  className="group inline-flex items-center gap-2 overline px-6 py-4 border border-white/20 text-white/80 hover:text-white hover:border-white/50 transition-colors">
                  <LinkIcon size={15} /> Copy Link
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <a href="/flyer/Tandem_T_31_Moms_Flyer.pdf" target="_blank" rel="noopener noreferrer" data-testid="dv-flyer-download"
                  className="group inline-flex items-center gap-3 overline px-7 py-4 bg-white text-black hover:bg-purple-100 transition-colors">
                  <Download size={15} /> Download the Flyer (PDF)
                </a>
              </div>
              <p className="text-white/40 text-xs font-light mt-4">
                Instagram doesn&rsquo;t allow direct web posting — we&rsquo;ll copy the link and open Instagram so you
                can paste it into your post or story.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WE ARE SEEKING */}
      <section className="relative z-10 py-20 md:py-28 px-6 md:px-10 border-t border-purple-400/15" data-testid="dv-seeking">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="overline text-purple-200">How You Can Help</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
              We are <span className="text-purple-300 italic">seeking.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SEEKING.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.tag} delay={i * 0.08}>
                  <div className="border border-purple-400/20 bg-white/[0.02] p-8 md:p-10 h-full" data-testid={`dv-seek-${i}`}>
                    <Icon size={26} className="text-purple-300" />
                    <h3 className="font-serif font-light text-white text-2xl md:text-3xl mt-6 leading-snug">{s.tag}</h3>
                    <p className="text-white/70 font-light leading-relaxed mt-4">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 border border-purple-400/25 bg-purple-500/[0.06] p-8 md:p-12 text-center max-w-3xl mx-auto">
              <p className="overline text-purple-200">Your support becomes something tangible</p>
              <p className="font-serif italic text-white text-xl md:text-2xl font-light leading-snug mt-5">
                It can become the toiletries a mother needs when starting over, comfort items for a child in an
                uncertain transition, transportation, essential supplies, or a New Beginning Bag that reminds a
                survivor her community has not forgotten her.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BECOME A COMMUNITY PARTNER — tiers, benefits, packet */}
      <section className="relative z-10 py-20 md:py-28 px-6 md:px-10 border-t border-purple-400/15" data-testid="dv-partner-tiers">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="overline text-purple-200">Become an October Community Partner</p>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
              A gift is appreciated. A commitment <span className="text-purple-300 italic">changes everything.</span>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-6 max-w-2xl">
              A one-time gift is greatly appreciated — but a long-term commitment creates an even greater impact.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.term} delay={i * 0.08}>
                <div className="border border-purple-400/20 bg-white/[0.02] p-8 h-full" data-testid={`dv-commitment-${i}`}>
                  <Ribbon className="w-6 h-8 text-purple-400" />
                  <p className="font-serif text-white text-2xl font-light mt-5">{c.term}</p>
                  <p className="text-white/65 font-light leading-relaxed mt-3">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-7">
              <div className="border border-purple-400/20 bg-white/[0.02] p-8 md:p-10 h-full">
                <p className="overline text-purple-200">Partner Benefits &amp; Recognition</p>
                <ul className="mt-6 space-y-4">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-white/80 font-light">
                      <Check size={18} className="text-purple-300 mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="border border-purple-400/30 bg-purple-500/[0.06] p-8 md:p-10 h-full flex flex-col">
                <FileText size={26} className="text-purple-300" />
                <h3 className="font-serif font-light text-white text-2xl md:text-3xl mt-5 leading-snug">
                  The full sponsor packet.
                </h3>
                <p className="text-white/70 font-light leading-relaxed mt-4 flex-1">
                  Everything at a glance — who we serve, the 31 Moms initiative, ways to give, commitment levels
                  and partner benefits. Share it with your team.
                </p>
                <a href="/packet/Tandem_T_Sponsor_Packet.pdf" target="_blank" rel="noopener noreferrer" data-testid="dv-packet-download"
                  className="group inline-flex items-center gap-3 mt-8 overline px-7 py-4 bg-white text-black hover:bg-purple-100 transition-colors">
                  <Download size={15} /> Download the Sponsor Packet (PDF)
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PARTNER FORM */}
      <section id="partner-form-section" className="relative z-10 py-20 md:py-28 px-6 md:px-10 border-t border-purple-400/15 scroll-mt-24" data-testid="dv-partner-form-section">
        <div className="max-w-[900px] mx-auto">
          <PartnerForm />
        </div>
      </section>

      {/* OCTOBER HOSTS */}
      <section className="relative z-10 py-20 md:py-28 px-6 md:px-10 border-t border-purple-400/15" data-testid="dv-hosts">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="flex items-center gap-3">
              <Heart size={18} className="text-purple-400" />
              <p className="overline text-purple-200">With Gratitude</p>
            </div>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
              Our October donation-bin <span className="text-purple-300 italic">hosts.</span>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-6 max-w-2xl">
              Thank you to the storefronts across Apollo Beach hosting our purple donation bins this month —
              helping us support 31 mothers, one for each day of October. These bins are dedicated solely to our
              domestic violence New Beginnings mission.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BIN_PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.06}>
                <div className="border border-purple-400/20 hover:border-purple-400/50 transition-colors h-full flex flex-col bg-white/[0.02]" data-testid={`dv-host-${i}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-purple-500/5 flex items-center justify-center">
                    {p.image ? (
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex flex-col items-center text-purple-300/70">
                        <HandHeart size={30} />
                        <span className="overline mt-3 text-purple-200/70">Your business here</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="overline text-purple-200/80">{p.status}</p>
                    <p className="font-serif text-white text-xl font-light mt-3 leading-snug">{p.name}</p>
                    <p className="flex items-start gap-2 text-white/50 text-sm font-light mt-3">
                      <MapPin size={13} className="mt-0.5 shrink-0 text-purple-300/70" /> {p.address}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16">
              <Link to="/foundation" data-testid="sponsors-back-link" className="group inline-flex items-center gap-3 overline text-white/60 hover:text-white transition-colors">
                <ArrowLeft size={15} /> Back to the Foundation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
